/**
 * AKU Management Tab - Atomic Knowledge Units
 * Extract, view, and manage AKUs from Knowledge Base documents
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';
import { 
  Atom, 
  FileText, 
  RefreshCw, 
  Search, 
  CheckCircle, 
  XCircle,
  Eye,
  Trash2,
  Sparkles,
  BookOpen,
  Brain,
  Target
} from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const getAuthHeaders = () => adminFetchHeaders(false);

// Certification options
const CERTIFICATIONS = [
  // Scrum.org
  { value: 'PSM1', label: 'PSM Level 1' },
  { value: 'PSM2', label: 'PSM Level 2' },
  { value: 'PSPO1', label: 'PSPO Level 1' },
  { value: 'PSPO2', label: 'PSPO Level 2' },
  // PMI
  { value: 'CAPM', label: 'CAPM' },
  { value: 'PMP', label: 'PMP' },
  // SAFe - Official certifications only
  { value: 'SAFe Agilist', label: 'SAFe Agilist (SA) - Leading SAFe' },
  { value: 'SAFe Practitioner', label: 'SAFe Practitioner (SP) - SAFe for Teams' },
  // AXELOS
  { value: 'ITIL4', label: 'ITIL 4 Foundation' },
  { value: 'PRINCE2', label: 'PRINCE2' }
];

// Status options
const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'reviewed', label: 'Reviewed', color: 'bg-blue-100 text-blue-800' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'archived', label: 'Archived', color: 'bg-gray-100 text-gray-800' }
];

const AdminAKUTab = ({ embedded = false, getAuthHeaders: propGetAuthHeaders }) => {
  // Use prop getAuthHeaders if provided (embedded mode), otherwise from context
  const contextAuth = useAuth();
  const getAuthHeaders = propGetAuthHeaders || contextAuth.getAuthHeaders;
  
  // State
  const [akus, setAkus] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  
  // Filters
  const [filterCert, setFilterCert] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  
  // Extraction dialog
  const [showExtractDialog, setShowExtractDialog] = useState(false);
  const [extractDocId, setExtractDocId] = useState('');
  const [extractCerts, setExtractCerts] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  
  // Detail dialog
  const [selectedAku, setSelectedAku] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Fetch AKUs
  const fetchAkus = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/aku/list?limit=100`;
      if (filterCert) url += `&certification=${filterCert}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (searchKeyword) url += `&keyword=${encodeURIComponent(searchKeyword)}`;
      
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setAkus(data.akus || []);
      }
    } catch (error) {
      console.error('Error fetching AKUs:', error);
    } finally {
      setLoading(false);
    }
  }, [filterCert, filterStatus, searchKeyword]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/aku/stats`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch KB documents
  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API}/knowledge-base/documents`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        // Handle both array and object response formats
        const docList = Array.isArray(data) ? data : (data.documents || []);
        // For AKU extraction, we need documents with PDF files available (not chunks!)
        // Show ALL documents - the extraction will check if PDF is available
        // Filter only removes documents that are explicitly marked as deleted or failed
        setDocuments(docList.filter(d => 
          d.status !== 'deleted' && 
          d.status !== 'failed'
        ));
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    fetchAkus();
    fetchStats();
    fetchDocuments();
  }, [fetchAkus]);

  // Extract AKUs from document
  const handleExtract = async () => {
    if (!extractDocId || extractCerts.length === 0) {
      toast.error('Please select a document and at least one certification');
      return;
    }
    
    setExtracting(true);
    setExtractResult(null);
    
    try {
      const response = await fetch(`${API}/aku/extract`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: extractDocId,
          certifications: extractCerts,
          max_akus: 50,
          include_misconceptions: true,
          include_sample_questions: true
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setExtractResult(result);
        
        // Show warnings if any
        if (result.warnings && result.warnings.length > 0) {
          // Show stats in toast if 0 AKUs
          if (result.akus_extracted === 0) {
            const statsWarning = result.warnings.find(w => w.startsWith('Stats:'));
            if (statsWarning) {
              toast.warning(`0 AKUs extrahiert.\n${statsWarning}`, { duration: 10000 });
            } else {
              toast.warning('0 AKUs extrahiert. Prüfen Sie die Browser-Konsole für Details.');
            }
            // Also show alert with full details
            let detailMsg = `Extraktion abgeschlossen: 0 AKUs\n\n`;
            detailMsg += `Dokument: ${result.document_name}\n`;
            detailMsg += `\nDetails:\n`;
            result.warnings.forEach(w => {
              detailMsg += `- ${w}\n`;
            });
            alert(detailMsg);
          } else {
            toast.success(`${result.akus_extracted} AKUs extrahiert`);
          }
        } else {
          toast.success(`${result.akus_extracted} AKUs extrahiert`);
        }
        
        fetchAkus();
        fetchStats();
      } else {
        const error = await response.json();
        // Show detailed error with debug info
        let errorMsg = 'Extraction failed';
        if (error.detail) {
          if (typeof error.detail === 'object') {
            errorMsg = error.detail.error || 'Unknown error';
            // Log debug info to console
            console.error('Extraction Debug Info:', error.detail);
            if (error.detail.debug) {
              const debug = error.detail.debug;
              errorMsg += `\n\nDebug:\n- Dokument gefunden: ${debug.document_found ? 'Ja' : 'Nein'}\n- Chunks: ${debug.chunks_found}`;
              if (debug.collections_checked) {
                debug.collections_checked.forEach(c => {
                  errorMsg += `\n  - ${c.name}: ${c.chunks}`;
                });
              }
            }
          } else {
            errorMsg = error.detail;
          }
        }
        toast.error(errorMsg, { duration: 10000 });
        alert(errorMsg); // Also show as alert for better visibility
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error('Extraction failed: ' + error.message);
    } finally {
      setExtracting(false);
    }
  };

  // Update AKU status
  const updateStatus = async (akuId, newStatus) => {
    try {
      const response = await fetch(`${API}/aku/${akuId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Status updated');
        fetchAkus();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Delete AKU
  const deleteAku = async (akuId) => {
    if (!window.confirm('Are you sure you want to delete this AKU?')) return;
    
    try {
      const response = await fetch(`${API}/aku/${akuId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('AKU deleted');
        fetchAkus();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete AKU');
    }
  };

  // Toggle certification selection
  const toggleCert = (cert) => {
    setExtractCerts(prev => 
      prev.includes(cert) 
        ? prev.filter(c => c !== cert)
        : [...prev, cert]
    );
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={opt.color}>{opt.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header - only show in standalone mode */}
      {!embedded && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Atom className="w-6 h-6" />
              Atomic Knowledge Units
            </h2>
            <p className="text-slate-500">Extract and manage testable knowledge units from KB documents</p>
          </div>
          <Dialog open={showExtractDialog} onOpenChange={setShowExtractDialog}>
            <DialogTrigger asChild>
              <Button data-testid="extract-akus-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Extract AKUs
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Extract AKUs from Document</DialogTitle>
                <DialogDescription>
                  Select a processed KB document to extract Atomic Knowledge Units.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Source Document *</Label>
                  <Select value={extractDocId} onValueChange={setExtractDocId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a document" />
                    </SelectTrigger>
                    <SelectContent>
                      {documents.map(doc => (
                        <SelectItem key={doc._id} value={doc._id}>
                          {doc.filename} ({doc.total_chunks || doc.chunks_count || 0} chunks)
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Target Certifications *</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {CERTIFICATIONS.map(cert => (
                    <button
                      key={cert.value}
                      type="button"
                      onClick={() => toggleCert(cert.value)}
                      className={`p-2 rounded-lg border-2 text-sm transition-all ${
                        extractCerts.includes(cert.value)
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {cert.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {extractResult && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-green-700 font-medium">
                    ✓ Extracted {extractResult.akus_extracted} AKUs in {extractResult.extraction_time_seconds.toFixed(1)}s
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExtractDialog(false)}>Cancel</Button>
              <Button onClick={handleExtract} disabled={extracting}>
                {extracting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {extracting ? 'Extracting...' : 'Extract'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      )}

      {/* Embedded mode: Show extract button inline */}
      {embedded && (
        <div className="flex justify-end">
          <Dialog open={showExtractDialog} onOpenChange={setShowExtractDialog}>
            <DialogTrigger asChild>
              <Button data-testid="extract-akus-btn" className="bg-purple-600 hover:bg-purple-700">
                <Sparkles className="w-4 h-4 mr-2" />
                AKUs aus Dokument extrahieren
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>AKUs aus Dokument extrahieren</DialogTitle>
                <DialogDescription>
                  Wählen Sie ein verarbeitetes KB-Dokument aus, um Atomic Knowledge Units zu extrahieren.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Quelldokument *</Label>
                  <Select value={extractDocId} onValueChange={setExtractDocId}>
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue placeholder="Dokument auswählen" className="truncate" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[500px]">
                      {documents.length === 0 ? (
                        <SelectItem value="none" disabled>Keine Dokumente - Bitte erst hochladen</SelectItem>
                      ) : (
                        documents.map(doc => (
                          <SelectItem key={doc.document_id || doc._id} value={doc.document_id || doc._id} className="truncate">
                            <span className="truncate block max-w-[450px]">
                              {doc.filename || doc.title} ({doc.total_chunks || doc.chunks_count || 0} chunks)
                            </span>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Ziel-Zertifizierungen *</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {CERTIFICATIONS.map(cert => (
                      <button
                        key={cert.value}
                        type="button"
                        onClick={() => toggleCert(cert.value)}
                        className={`p-2 rounded-lg border-2 text-xs font-medium transition-all text-center ${
                          extractCerts.includes(cert.value)
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                        }`}
                      >
                        {cert.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Debug Button */}
                {extractDocId && (
                  <div className="pt-2 border-t flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-xs text-gray-500"
                      onClick={async () => {
                        try {
                          // URL encode the document ID
                          const encodedDocId = encodeURIComponent(extractDocId);
                          
                          const response = await fetch(`${API}/aku/debug-document/${encodedDocId}`, {
                            headers: getAuthHeaders()
                          });
                          
                          if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`HTTP ${response.status}: ${errorText}`);
                          }
                          
                          const debug = await response.json();
                          
                          let message = `📄 Dokument: ${debug.document_found ? '✅ Gefunden' : '❌ Nicht gefunden'}\n`;
                          if (debug.document_info) {
                            message += `   Datei: ${debug.document_info.filename || debug.document_info.title || 'unbekannt'}\n`;
                            message += `   Status: ${debug.document_info.status || 'unbekannt'}\n`;
                          }
                          message += `📦 Chunks: ${debug.chunks_found}\n`;
                          if (debug.chunks_by_collection && Object.keys(debug.chunks_by_collection).length > 0) {
                            Object.entries(debug.chunks_by_collection).forEach(([coll, count]) => {
                              message += `   - ${coll}: ${count}\n`;
                            });
                          }
                          if (debug.all_kb_collections?.length > 0) {
                            message += `\n📁 KB Collections in DB: ${debug.all_kb_collections.length}\n`;
                          }
                          message += `🤖 LLM: ${debug.llm_available ? '✅ Verfügbar' : '❌ Nicht verfügbar'}\n`;
                          if (debug.issues?.length > 0) {
                            message += `\n⚠️ Probleme:\n`;
                            debug.issues.forEach(issue => {
                              message += `   - ${issue}\n`;
                            });
                          }
                          if (debug.note) {
                            message += `\n📝 Hinweis: ${debug.note}\n`;
                          }
                          if (debug.content_field_used) {
                            message += `\n📝 Content-Feld: ${debug.content_field_used} (${debug.sample_content_length} Zeichen)`;
                          }
                          
                          alert(message);
                        } catch (error) {
                          console.error('Debug error:', error);
                          // Show more detailed error info
                          let errorMsg = 'Debug-Fehler:\n\n';
                          errorMsg += `Fehler: ${error.message}\n\n`;
                          errorMsg += `Document ID: ${extractDocId}\n`;
                          errorMsg += `API URL: ${API}/aku/debug-document/...\n\n`;
                          errorMsg += 'Mögliche Ursachen:\n';
                          errorMsg += '- Endpoint nicht deployed\n';
                          errorMsg += '- Server-Fehler\n';
                          errorMsg += '- Netzwerk-Problem\n\n';
                          errorMsg += 'Bitte Browser-Konsole (F12) für Details prüfen.';
                          alert(errorMsg);
                        }
                      }}
                    >
                      🔍 Debug Dokument
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-xs text-gray-500"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/aku/debug-kb-status`, {
                            headers: getAuthHeaders()
                          });
                          
                          if (!response.ok) {
                            throw new Error(`HTTP ${response.status}`);
                          }
                          
                          const status = await response.json();
                          
                          let message = `📚 Knowledge Base Status\n\n`;
                          message += `📄 Dokumente: ${status.kb_documents_count}\n`;
                          message += `📁 Collections: ${status.collections?.length || 0}\n\n`;
                          
                          if (status.total_chunks_by_collection && Object.keys(status.total_chunks_by_collection).length > 0) {
                            message += `📦 Chunks pro Collection:\n`;
                            Object.entries(status.total_chunks_by_collection).forEach(([coll, count]) => {
                              message += `   - ${coll}: ${count}\n`;
                            });
                          }
                          
                          if (status.documents?.length > 0) {
                            message += `\n📋 Erste Dokumente:\n`;
                            status.documents.forEach(doc => {
                              message += `   - ${doc.filename || doc.document_id}: ${doc.chunks_count || 0} chunks\n`;
                            });
                          }
                          
                          alert(message);
                        } catch (error) {
                          alert('KB Status Fehler: ' + error.message);
                        }
                      }}
                    >
                      📚 KB Status
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-xs text-blue-500"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/aku/test-extract-single/${encodeURIComponent(extractDocId)}`, {
                            method: 'POST',
                            headers: getAuthHeaders()
                          });
                          
                          if (!response.ok) {
                            throw new Error(`HTTP ${response.status}`);
                          }
                          
                          const result = await response.json();
                          
                          let message = `🧪 Test-Extraktion\n\n`;
                          message += `Dokument: ${result.document_id}\n\n`;
                          message += `Schritte:\n`;
                          result.steps?.forEach(step => {
                            message += `${step}\n`;
                          });
                          
                          if (result.chunk_fields) {
                            message += `\n📋 Chunk-Felder: ${result.chunk_fields.join(', ')}\n`;
                          }
                          
                          if (result.content_preview) {
                            message += `\n📝 Content-Vorschau:\n"${result.content_preview}"\n`;
                          }
                          
                          if (result.extraction_result) {
                            message += `\n✅ Extraktion würde funktionieren!\n`;
                            message += `Konzept: ${result.extraction_result.concept_name}\n`;
                          }
                          
                          if (result.error) {
                            message += `\n❌ Fehler: ${result.error}\n`;
                          }
                          
                          alert(message);
                        } catch (error) {
                          alert('Test-Fehler: ' + error.message);
                        }
                      }}
                    >
                      🧪 Test Extraktion
                    </Button>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowExtractDialog(false)}>Abbrechen</Button>
                <Button onClick={handleExtract} disabled={extracting || !extractDocId || extractCerts.length === 0}>
                  {extracting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {extracting ? 'Extrahiere...' : 'Extrahieren'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-sm text-slate-500">Total AKUs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.active || 0}</div>
              <p className="text-sm text-slate-500">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-yellow-600">{stats.by_status?.draft || 0}</div>
              <p className="text-sm text-slate-500">Draft</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">{stats.by_status?.reviewed || 0}</div>
              <p className="text-sm text-slate-500">Reviewed</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by keyword..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchAkus()}
              />
            </div>
            <Select value={filterCert || "all"} onValueChange={(v) => setFilterCert(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Certification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Certs</SelectItem>
                {CERTIFICATIONS.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={fetchAkus} variant="outline">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button 
              onClick={async () => {
                try {
                  const response = await fetch(`${API}/aku/repair-all`, {
                    method: 'POST',
                    headers: getAuthHeaders()
                  });
                  const result = await response.json();
                  if (response.ok) {
                    toast.success(`Repariert: ${result.fixed_category} Kategorien, ${result.fixed_certification} Zertifizierungen`);
                    fetchAkus();
                    fetchStats();
                  } else {
                    toast.error(result.detail || 'Reparatur fehlgeschlagen');
                  }
                } catch (error) {
                  toast.error('Fehler bei der Reparatur');
                }
              }}
              variant="outline"
              size="sm"
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Daten reparieren
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AKU List */}
      <Card>
        <CardHeader>
          <CardTitle>AKU Library</CardTitle>
          <CardDescription>
            {akus.length} AKUs found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            </div>
          ) : akus.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Atom className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No AKUs found.</p>
              <p className="text-sm">Extract AKUs from your Knowledge Base documents to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Concept</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Cert</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {akus.map(aku => (
                  <TableRow key={aku._id}>
                    <TableCell className="font-mono text-sm">{aku.aku_id}</TableCell>
                    <TableCell className="max-w-xs truncate">{aku.concept_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{aku.concept_category}</Badge>
                    </TableCell>
                    <TableCell>
                      {Array.isArray(aku.certification) 
                        ? aku.certification.slice(0, 2).map(c => (
                            <Badge key={c} variant="secondary" className="mr-1">{c}</Badge>
                          ))
                        : aku.certification && (
                            <Badge variant="secondary">{aku.certification}</Badge>
                          )
                      }
                    </TableCell>
                    <TableCell>{getStatusBadge(aku.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setSelectedAku(aku); setShowDetailDialog(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {aku.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateStatus(aku.aku_id, 'active')}
                            className="text-green-600"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteAku(aku.aku_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              {selectedAku?.concept_name}
            </DialogTitle>
            <DialogDescription>
              {selectedAku?.aku_id} • {selectedAku?.concept_category}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAku && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Definition</Label>
                <p className="mt-1">{selectedAku.definition}</p>
              </div>
              
              {selectedAku.key_points?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Key Points</Label>
                  <ul className="mt-1 list-disc list-inside">
                    {selectedAku.key_points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedAku.misconceptions?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Common Misconceptions (for Distractors)</Label>
                  <div className="mt-1 space-y-2">
                    {selectedAku.misconceptions.map((m, i) => (
                      <div key={i} className="p-2 bg-red-50 rounded text-sm">
                        <p className="font-medium text-red-700">✗ {m.misconception_text}</p>
                        <p className="text-red-600 text-xs mt-1">{m.why_wrong}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Source</Label>
                  <p className="text-sm">{selectedAku.source_document_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Certifications</Label>
                  <div className="flex gap-1 mt-1">
                    {Array.isArray(selectedAku.certification) 
                      ? selectedAku.certification.map(c => (
                          <Badge key={c} variant="secondary">{c}</Badge>
                        ))
                      : selectedAku.certification && (
                          <Badge variant="secondary">{selectedAku.certification}</Badge>
                        )
                    }
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Select 
                  value={selectedAku.status} 
                  onValueChange={(val) => {
                    updateStatus(selectedAku.aku_id, val);
                    setSelectedAku({...selectedAku, status: val});
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAKUTab;
